// react
import { SyntheticEvent } from 'react';

// l18n
import { TFunction } from 'i18next';

// mui
import { Box, Grid, Checkbox as MuiCheckbox, Typography, TypographyProps } from '@mui/material';

// table
import { TableHeader } from '../../../ui/Table/Table';

// types
import {
	Account,
	Category,
	Comment,
	Component,
	Contract,
	Entity,
	ExpenseType,
	Metadata,
	MetadataField,
	TransactionWithDaEquivalent,
	TransactionStatus,
	TransactionType,
	WithdrawalApplication,
	Transaction,
} from '../../../../@types';
import { financiersWbg } from '../../../../@types/unions';

// ui
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { FilterProps } from '../../../ui/Filters/Filters';
import { Time } from '../../../ui/Time/Time';

// links
import { LinkContract } from '../../../links/LinkContract/LinkContract';
import { LinkEntity } from '../../../links/LinkEntity/LinkEntity';
import { LinkAccount } from '../../../links/LinkAccount/LinkAccount';

// utils
import { translateTransactionType } from '../../../../utils/translations';
import { formatCurrency } from '../../../../utils/currency';

// theme
import { theme } from '../../../../theme';

// props
export const headersIncludedDefault: string[] = [
	'amountLoan',
	'amountLoanDaEquivalent',
	'category',
	'comments',
	'component',
	'contract',
	'created',
	'currency',
	'currencyDa',
	'date',
	'designatedAccount',
	'exchangeRateBaseCurrency',
	'exchangeRateDa',
	'exchangeRateQuoteCurrency',
	'expenseType',
	'fromAccount',
	'fromEntity',
	'id',
	'metadata',
	'modified',
	'status',
	'stepId',
	'taxLoan',
	'taxLoanDaEquivalent',
	'toEntity',
	'totalLoanDaEquivalent',
	'type',
	'withdrawalApplication',
];

export interface DocumentHeaderTableProps {
	filters?: FilterProps[];
	handleChangeCheckBox?: (e: SyntheticEvent<Element>, id: number) => void;
	headersIncluded?: string[];
	headersVisible?: string[];
	t: TFunction<'translation', undefined>;
	transaction?: TransactionWithDaEquivalent;
	transactionsUnchecked?: number[];
}

export const getTransactionsHeaders = ({
	filters,
	headersIncluded = headersIncludedDefault,
	headersVisible,
	handleChangeCheckBox,
	t,
	transactionsUnchecked,
}: DocumentHeaderTableProps): TableHeader[] => {
	// vars
	const loanFilter = filters?.find((f) => f.name === 'loan');

	let percentFinancedLabel = t('percentFinanced');
	if (loanFilter) {
		const loanOption = loanFilter.options?.find((opt) => opt.value === loanFilter.value);
		if (
			loanOption?.MenuItemProps?.financier &&
			financiersWbg.includes(loanOption?.MenuItemProps?.financier)
		) {
			percentFinancedLabel = t('percentFinancedByFinancier', {
				financier: loanOption.MenuItemProps?.financier,
			});
		}
	}

	const headers: TableHeader[] = [
		{
			checked: true,
			key: 'checkbox',
			label: t(''),
			value: '',
			render: (value: null, header: TableHeader, transaction: TransactionWithDaEquivalent) => {
				if (handleChangeCheckBox && transactionsUnchecked) {
					return (
						<MuiCheckbox
							checked={!transactionsUnchecked.includes(transaction.id)}
							onChange={(e) => handleChangeCheckBox(e, transaction.id)}
						/>
					);
				}
				return null;
			},
		},
		{
			checked: false,
			key: 'id',
			label: t('id'),
			value: 'id',
			render: (id: number) => {
				return <Typography>{id}</Typography>;
			},
		},
		{
			checked: false,
			key: 'type',
			label: t('type'),
			value: 'type',
			render: (type: TransactionType) => {
				return <Typography fontWeight={600}>{translateTransactionType(type, t)}</Typography>;
			},
		},
		{
			checked: true,
			key: 'toEntity',
			label: t('to'),
			value: 'toEntity',
			render: (toEntity: Entity, _header: TableHeader, transaction: Transaction) => {
				if (transaction.type === 'OPEX') {
					return <Typography>{transaction.metadata?.vendor || t('na')}</Typography>;
				}
				return <LinkEntity entity={toEntity} />;
			},
		},
		{
			checked: false,
			key: 'fromEntity',
			label: t('from'),
			value: 'fromEntity',
			render: (fromEntity: Entity) => {
				return <LinkEntity entity={fromEntity} />;
			},
		},
		{
			checked: false,
			key: 'fromAccount',
			label: t('fromAccount'),
			value: 'fromAccount',
			render: (fromAccount: Account) => {
				return <LinkAccount account={fromAccount} />;
			},
		},
		{
			checked: false,
			key: 'designatedAccount',
			label: t('designatedAccount'),
			value: 'designatedAccount',
			render: (designatedAccount: Account) => {
				return <LinkAccount account={designatedAccount} />;
			},
		},
		{
			checked: true,
			key: 'contract',
			label: t('contract'),
			value: 'contract',
			render: (contract: Contract) => {
				return contract ? <LinkContract contract={contract} /> : <Typography>{t('na')}</Typography>;
			},
		},
		{
			checked: true,
			key: 'contractTotalAmount',
			label: t('contractTotalAmount'),
			value: 'contract',
			render: (
				contract: Contract,
				_header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				const toEntity = transaction.toEntity as Entity;
				const isBeneficiary = toEntity?.type === 'BENEFICIARY';

				if (isBeneficiary) {
					const parsedMetadata: MetadataField[] = Array.isArray(toEntity?.metadata)
						? (toEntity.metadata as MetadataField[])
						: [];
					const metadataTotalBenefits = parsedMetadata.find((m) => m.name === 'totalBenefits');
					const metadataCurrency = parsedMetadata.find((m) => m.name === 'currency');

					if (metadataTotalBenefits?.value) {
						return (
							<Typography>
								{metadataCurrency?.value && metadataTotalBenefits?.value
									? formatCurrency({
											currency: String(metadataCurrency.value),
											value: Number(metadataTotalBenefits.value),
										})
									: metadataTotalBenefits.value}
							</Typography>
						);
					}
				} else if (contract?.amounts?.length) {
					return (
						<Grid container>
							{contract.amounts.map((amt, index) => (
								<Grid item key={`${amt.currency}-${index}`} xs={12}>
									<Typography component="span">
										{formatCurrency({ currency: amt.currency, value: amt.amount })}
									</Typography>
								</Grid>
							))}
						</Grid>
					);
				}
				return <Typography>{t('na')}</Typography>;
			},
		},
		{
			checked: false,
			key: 'stepId',
			label: t('stepId'),
			value: 'stepId',
			render: (contract: Contract) => {
				return <Typography>{contract ? contract?.idStep : t('na')}</Typography>;
			},
		},
		{
			checked: false,
			key: 'component',
			label: t('component'),
			value: 'component',
			render: (component: Component) => {
				return <Typography>{component ? component.name : t('na')}</Typography>;
			},
		},
		{
			checked: false,
			key: 'category',
			label: t('category'),
			value: 'category',
			render: (category: Category) => {
				return <Typography>{category ? category.name : t('na')}</Typography>;
			},
		},
		{
			checked: true,
			key: 'expenseType',
			label: t('expenseType'),
			value: 'expenseType',
			render: (expenseType: ExpenseType) => {
				return <Typography>{expenseType ? expenseType.name : t('na')}</Typography>;
			},
		},
		{
			checked: true,
			key: 'invoiceAmount',
			label: t('invoiceAmount'),
			value: 'metadata',
			render: (metadata: Metadata, _header: TableHeader, transaction: Transaction) => {
				if (transaction.type !== 'PAYMENT') {
					return <Typography>{t('na')}</Typography>;
				}
				return (
					<Typography>
						{metadata && metadata.invoiceAmount && metadata.invoiceCurrency
							? formatCurrency({
									currency: metadata.invoiceCurrency as string,
									value: Number(metadata.invoiceAmount),
								})
							: t('na')}
					</Typography>
				);
			},
		},
		{
			checked: true,
			key: 'invoiceCurrency',
			label: t('invoiceCurrency'),
			value: 'metadata',
			render: (metadata: Metadata, _header: TableHeader, transaction: Transaction) => {
				if (transaction.type !== 'PAYMENT') {
					return <Typography>{t('na')}</Typography>;
				}
				return <Typography>{metadata?.invoiceCurrency || t('na')}</Typography>;
			},
		},
		{
			checked: true,
			key: 'percentFinanced',
			label: percentFinancedLabel,
			value: 'metadata',
			render: (
				metadata: Metadata,
				header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				return <Typography>{t('na')}</Typography>;
			},
		},
		{
			checked: true,
			key: 'currency',
			label: t('currency'),
			value: 'currency',
			render: (currency: string) => {
				return <Typography>{currency}</Typography>;
			},
		},
		{
			checked: true,
			key: 'amountLoan',
			label: t('amount'),
			value: 'amountLoan',
			render: (
				amountLoan: number,
				header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				const TypographyProps: Partial<TypographyProps> = {};
				let value = amountLoan;

				// display refunds as negative values
				if (transaction.type === 'PAYMENT_REFUND') {
					TypographyProps.color = 'error.main';
					value = amountLoan * -1;
				}

				return (
					<Typography {...TypographyProps}>
						{formatCurrency({ currency: transaction.currency, value })}
					</Typography>
				);
			},
		},
		{
			checked: true,
			key: 'taxLoan',
			label: t('tax'),
			value: 'taxLoan',
			render: (taxLoan: number, header: TableHeader, transaction: TransactionWithDaEquivalent) => {
				return formatCurrency({ currency: transaction.currency, value: taxLoan });
			},
		},
		{
			checked: true,
			key: 'totalRefundedLoan',
			label: t('refunded'),
			value: 'totalRefundedLoan',
			render: (
				totalRefundedLoan: number,
				header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				let content = t('na');
				if (totalRefundedLoan !== null) {
					content = formatCurrency({ currency: transaction.currency, value: totalRefundedLoan });
				}
				return <Typography>{content}</Typography>;
			},
		},
		{
			checked: true,
			key: 'currencyDa',
			label: `${t('currency')} (${t('daEquivalent')})`,
			value: 'currencyDa',
			render: (currencyDa: string) => {
				return <Typography>{currencyDa || t('na')}</Typography>;
			},
		},
		{
			checked: true,
			key: 'amountLoanDaEquivalent',
			label: `${t('amount')} (${t('daEquivalent')})`,
			value: 'amountLoanDaEquivalent',
			render: (
				amountLoanDaEquivalent: number,
				header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				const isDaCurrency = transaction.currency === transaction.currencyDa;
				const TypographyProps: Partial<TypographyProps> = {};

				let content: null | number | string = t('na');

				if (amountLoanDaEquivalent) {
					content = amountLoanDaEquivalent;
				} else if (isDaCurrency) {
					content = transaction.amountLoan;
				}

				if (typeof content === 'number') {
					// display refunds as negative values
					if (transaction.type === 'PAYMENT_REFUND') {
						TypographyProps.color = 'error.main';
						content = content * -1;
					}

					content = formatCurrency({
						currency: transaction.currencyDa,
						value: content,
					});
				}
				return <Typography {...TypographyProps}>{content}</Typography>;
			},
		},
		{
			checked: true,
			key: 'taxLoanDaEquivalent',
			label: `${t('tax')} (${t('daEquivalent')})`,
			value: 'taxLoanDaEquivalent',
			render: (
				taxLoanDaEquivalent: number,
				header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				const isDaCurrency = transaction.currency === transaction.currencyDa;

				let content: null | number | string = t('na');

				if (taxLoanDaEquivalent) {
					content = taxLoanDaEquivalent;
				} else if (isDaCurrency) {
					content = transaction.taxLoan;
				}

				if (typeof content === 'number') {
					content = formatCurrency({
						currency: transaction.currencyDa,
						value: content,
					});
				}

				return <Typography>{content}</Typography>;
			},
		},
		{
			checked: true,
			key: 'totalRefundedLoanDaEquivalent',
			label: `${t('refunded')} (${t('daEquivalent')})`,
			value: 'totalRefundedLoanDaEquivalent',
			render: (
				totalRefundedLoanDaEquivalent: number,
				header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				if (totalRefundedLoanDaEquivalent !== null && transaction.currencyDa) {
					return formatCurrency({
						currency: transaction.currencyDa,
						value: totalRefundedLoanDaEquivalent,
					});
				}
				return <Typography>{t('na')}</Typography>;
			},
		},
		{
			checked: true,
			key: 'totalPaidFromDa',
			label: t('totalPaidFromDa'),
			value: 'totalLoanDaEquivalent',
			render: (
				totalLoanDaEquivalent: number,
				header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				if (
					transaction.type !== 'PAYMENT_REFUND' &&
					transaction.totalLoanDaEquivalent !== null &&
					transaction.currencyDa
				) {
					return formatCurrency({
						currency: transaction.currencyDa,
						value: transaction.totalLoanDaEquivalent,
					});
				}
				return <Typography>{t('na')}</Typography>;
			},
		},
		{
			checked: true,
			key: 'exchangeRateDa',
			label: t('exchangeRate'),
			value: 'exchangeRateDa',
			render: (
				exchangeRateDa: string,
				header: TableHeader,
				transaction: TransactionWithDaEquivalent
			) => {
				if (exchangeRateDa) {
					return <Typography>{exchangeRateDa}</Typography>;
				}
				return <Typography>{t('na')}</Typography>;
			},
		},
		{
			checked: false,
			key: 'exchangeRateBaseCurrency',
			label: t('exchangeRateBaseCurrency'),
			value: 'exchangeRateBaseCurrency',
			render: (exchangeRateBaseCurrency: string) => {
				return <Typography>{exchangeRateBaseCurrency || t('na')}</Typography>;
			},
		},
		{
			checked: false,
			key: 'exchangeRateQuoteCurrency',
			label: t('exchangeRateQuoteCurrency'),
			value: 'exchangeRateQuoteCurrency',
			render: (exchangeRateQuoteCurrency: string) => {
				return <Typography>{exchangeRateQuoteCurrency || t('na')}</Typography>;
			},
		},
		{
			checked: false,
			key: 'withdrawalApplication',
			label: t('withdrawalApplication'),
			value: 'withdrawalApplication',
			render: (withdrawalApplication: WithdrawalApplication) => {
				return (
					<Typography>
						{withdrawalApplication ? withdrawalApplication.idExternal : t('na')}
					</Typography>
				);
			},
		},
		{
			checked: true,
			key: 'date',
			label: t('dateOfPayment'),
			value: 'date',
			render: (date: Date) => {
				return <Time date={new Date(date)} />;
			},
		},
		{
			checked: false,
			key: 'status',
			label: t('status'),
			value: 'status',
			render: (status: TransactionStatus) => {
				return <ChipStatus status={status} />;
			},
		},
		{
			checked: true,
			key: 'comments',
			label: t('comments'),
			value: 'comments',
			render: (comments: Comment[]) => {
				if (Array.isArray(comments) && comments.length > 0) {
					return (
						<Box component="ul" sx={{ margin: 0, padding: theme.spacing(0, 0, 0, 2) }}>
							{comments.map((comment) => (
								<Box component="li">
									<Typography>{comment.comment}</Typography>
								</Box>
							))}
						</Box>
					);
				}
				return <Typography>{t('na')}</Typography>;
			},
		},
		{
			checked: false,
			key: 'created',
			label: t('created'),
			value: 'created',
			render: (created: Date) => {
				return <Time date={new Date(created)} />;
			},
		},
		{
			checked: false,
			key: 'modified',
			label: t('modified'),
			value: 'modified',
			render: (modified: Date) => {
				return <Time date={new Date(modified)} />;
			},
		},
	];

	// Filter headers based on included values
	let filteredHeaders = headers.filter(
		(col) => col.value === '' || headersIncluded.includes(col.value)
	);

	// Filter headers based on visibility if provided
	if (headersVisible) {
		filteredHeaders = filteredHeaders.filter(
			(col) => col.value === '' || headersVisible.includes(col.key || col.value)
		);
	}

	return filteredHeaders;
};

export interface HeaderSelection {
	checked: boolean;
	key: string;
	label: string;
}

export interface GetHeaderSelectionsParams {
	filters?: FilterProps[];
	headersIncluded?: string[];
	t: TFunction<'translation', undefined>;
}

/**
 * Returns header selections for use in column visibility controls.
 * Excludes the checkbox column (key: 'checkbox').
 */
export const getHeaderSelections = ({
	filters,
	headersIncluded = headersIncludedDefault,
	t,
}: GetHeaderSelectionsParams): HeaderSelection[] => {
	const headers = getTransactionsHeaders({ filters, t });

	// Filter out checkbox column and map to HeaderSelection format
	return headers
		.filter(
			(header) =>
				header.key && header.key !== 'checkbox' && headersIncluded.includes(header.value as string)
		)
		.map((header) => ({
			checked: header.checked ?? false,
			key: header.key as string,
			label: typeof header.label === 'string' ? header.label : '',
		}));
};

/**
 * Returns a map of header keys to their labels.
 */
export const getHeaderLabelMap = ({
	filters,
	t,
}: GetHeaderSelectionsParams): Record<string, string> => {
	const headerSelections = getHeaderSelections({ filters, t });
	const headerLabelMap: Record<string, string> = {};

	headerSelections.forEach((header) => {
		headerLabelMap[header.key] = header.label;
	});

	return headerLabelMap;
};
